import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

interface HeroSolidBGProps {
  tokenPrefix?: string;
  title?: string;
  /** H2 subheading shown under the page title. */
  subtitle?: string;
  blurbField?: string;
  defaultText?: string;
  /** When false, hide the page blurb (title-only banner). Defaults to true. */
  showBlurb?: boolean;
  /** When true, render a photo behind the banner instead of the solid primary fill. */
  showBackgroundImage?: boolean;
  /** Kept for pages.json prop compatibility; not rendered in this layout. */
  ctaLabel?: string;
  /** Kept for pages.json prop compatibility; not rendered in this layout. */
  ctaUrl?: string;
}

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Page banner',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function HeroSolidBG({
  tokenPrefix,
  title = 'Page',
  subtitle,
  blurbField,
  defaultText,
  showBlurb = true,
  showBackgroundImage = false,
}: HeroSolidBGProps) {
  const prefix = tokenPrefix || 'herosolidbg';
  const titlePath = `${prefix}.title`;
  const subtitlePath = `${prefix}.subtitle`;
  const blurbPath = `${prefix}.blurb`;
  const bgPath = `${prefix}.bg_image`;

  const bgImage = useSlotGroup(bgPath, bgImageDef);

  // Fluid type + no mid-word breaks: long uppercase titles (e.g. Accessibility)
  // must scale down on smaller desktops instead of wrapping mid-word.
  const titleClassName =
    'font-heading font-bold uppercase leading-[1.15] tracking-[0.1em] xl:tracking-[0.12em] text-balance hyphens-none [overflow-wrap:normal] [word-break:normal] text-[clamp(1.75rem,0.85rem+2.2vw,3rem)]';
  const subtitleClassName =
    'font-body mt-3 text-base font-normal leading-snug tracking-wide opacity-90 sm:text-lg md:text-xl';
  const blurbClassName =
    'font-body text-base leading-relaxed opacity-95 md:text-lg [&_a]:underline [&_a]:underline-offset-2 [&_a]:decoration-from-font';

  const titleBlock = (
    <div className="min-w-0">
      <RichTextContent
        path={titlePath}
        defaultValue={`<p>${title}</p>`}
        as="h1"
        className={titleClassName}
      />
      <RichTextContent
        path={subtitlePath}
        defaultValue={subtitle ? `<p>${subtitle}</p>` : ''}
        as="h2"
        className={subtitle ? subtitleClassName : 'hidden'}
        style={subtitle ? undefined : { display: 'none' }}
      />
    </div>
  );

  const content = showBlurb ? (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between lg:gap-12 xl:gap-20">
      {/* Page Title + Subtitle — left; full width until lg so long titles aren't crushed */}
      <div className="min-w-0 lg:max-w-[48%] xl:max-w-[42%]">{titleBlock}</div>

      {/* Page Blurb — right */}
      <div className="min-w-0 lg:max-w-xl xl:max-w-2xl">
        <RichTextContent
          path={blurbPath}
          defaultField={blurbField}
          defaultValue={defaultText || ''}
          as="div"
          className={blurbClassName}
        />
      </div>
    </div>
  ) : (
    titleBlock
  );

  return (
    <section
      className={
        showBackgroundImage
          ? 'relative overflow-hidden text-primary-foreground'
          : 'bg-primary text-primary-foreground'
      }
    >
      {showBackgroundImage ? (
        <EditableSlotGroup
          groupPath={bgPath}
          definition={bgImageDef}
          className="absolute inset-0"
        >
          {bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || 'Page banner'}
              sizes="100vw"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full bg-primary" />
          )}
          <div className="absolute inset-0 bg-black/45" aria-hidden />
        </EditableSlotGroup>
      ) : null}

      {/*
        Mobile header overlays the hero (transparent → primary on scroll), so
        pad the banner top by offer-banner + header height below lg. Desktop
        keeps the solid-header spacer, so only content padding applies at lg+.
      */}
      <div className="relative z-10 mx-auto max-w-7xl px-6 pb-10 pt-[calc(var(--offer-banner-height,0px)+5rem)] sm:px-8 md:pb-12 lg:py-12 xl:px-10">
        {content}
      </div>

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="herosolidbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.subtitle"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="herosolidbg.bg_image"
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSolidBG',
  displayName: 'Hero – Solid Background',
  tokens: {
    'herosolidbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'herosolidbg.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '',
    },
    'herosolidbg.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'herosolidbg.bg_image': bgImageDef,
  },
};

(HeroSolidBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSolidBG;
